import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

const errorText = (err, fallback) => err?.response?.data?.error || fallback;

/**
 * The manual selection's palette as the picking pages see it.
 *
 * Loads the picks once, then applies every add / remove through
 * ``api/selection/manual/samples``. The requests run one after the other, so
 * quick clicks on several patches never land out of order; every answer
 * carries the whole palette and replaces the local copy.
 *
 * While a change is on its way, ``isPicked`` already answers with the new
 * state (the patch shows its check at once); the panel lists the confirmed
 * palette.
 *
 * Pass ``null`` as the selection id to hold off loading (e.g. until the page
 * has checked the selection's type and status).
 */
export default function useManualPicks(selectionId) {
    const [picks, setPicks] = useState([]);
    const [loading, setLoading] = useState(Boolean(selectionId));
    const [error, setError] = useState(null);
    // sampleId -> { op: "add" | "remove", seq } of changes on their way
    const [pending, setPending] = useState({});
    const queue = useRef(Promise.resolve());
    const seq = useRef(0);
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (!selectionId) {
            setLoading(false);
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        axios
            .post("api/selection/manual/samples", { selectionId })
            .then((response) => {
                if (!cancelled) setPicks(response.data.picks || []);
            })
            .catch((err) => {
                if (!cancelled) setError(errorText(err, "Loading the palette failed."));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [selectionId]);

    const pickedIds = useMemo(() => new Set(picks.map((pick) => String(pick.sample_id))), [picks]);

    const change = useCallback(
        ({ add = [], remove = [] }) => {
            const addIds = add.map(String);
            const removeIds = remove.map(String);
            const mySeq = ++seq.current;
            setPending((prev) => {
                const next = { ...prev };
                addIds.forEach((id) => { next[id] = { op: "add", seq: mySeq }; });
                removeIds.forEach((id) => { next[id] = { op: "remove", seq: mySeq }; });
                return next;
            });
            const run = async () => {
                try {
                    const response = await axios.post("api/selection/manual/samples", {
                        selectionId,
                        add: addIds,
                        remove: removeIds,
                    });
                    if (mounted.current) {
                        setPicks(response.data.picks || []);
                        setError(null);
                    }
                    return true;
                } catch (err) {
                    if (mounted.current) setError(errorText(err, "Changing the palette failed."));
                    return false;
                } finally {
                    if (mounted.current) {
                        setPending((prev) => {
                            const next = { ...prev };
                            [...addIds, ...removeIds].forEach((id) => {
                                // A later click on the same sample keeps its own entry.
                                if (next[id] && next[id].seq === mySeq) delete next[id];
                            });
                            return next;
                        });
                    }
                }
            };
            const result = queue.current.then(run);
            queue.current = result.catch(() => {});
            return result;
        },
        [selectionId]
    );

    const isPicked = useCallback(
        (sampleId) => {
            const entry = pending[String(sampleId)];
            if (entry) return entry.op === "add";
            return pickedIds.has(String(sampleId));
        },
        [pending, pickedIds]
    );

    const isPending = useCallback((sampleId) => Boolean(pending[String(sampleId)]), [pending]);

    const toggle = useCallback(
        (sampleId) => (isPicked(sampleId) ? change({ remove: [sampleId] }) : change({ add: [sampleId] })),
        [isPicked, change]
    );

    return {
        picks,
        loading,
        error,
        clearError: () => setError(null),
        isPicked,
        isPending,
        toggle,
        add: (sampleIds) => change({ add: sampleIds }),
        remove: (sampleIds) => change({ remove: sampleIds }),
        clear: () => change({ remove: picks.map((pick) => pick.sample_id) }),
    };
}
